<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  document: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const signatureLine =
  '.............................................................................';
const witnessLine =
  '.........................................................................';
const witnessSignature = index => `${index + 1}. ${witnessLine}`;
const content = computed(() => props.document || {});
const clauses = computed(() => content.value.clauses || []);
const sellers = computed(() => content.value.sellers || []);
const buyers = computed(() => content.value.buyers || []);
const sellerSignatures = computed(() => content.value.seller_signatures || []);
const buyerSignatures = computed(() => content.value.buyer_signatures || []);
const witnesses = computed(() => content.value.witnesses || []);
</script>

<template>
  <article
    class="mx-auto mb-24 w-full max-w-[1160px] rounded-[10px] border border-lais-border-soft bg-white shadow-sm"
    data-test="sales-receipt-draft-document"
  >
    <div class="p-5 sm:p-10 lg:px-[118px] lg:py-20">
      <div
        class="mb-8 flex justify-center text-sm font-semibold uppercase tracking-[0.16em] text-lais-primary"
      >
        {{ content.title }}
      </div>

      <section class="space-y-5 text-sm leading-6 text-lais-text">
        <div>
          <p class="font-semibold">{{ content.sellers_heading }}</p>
          <p v-for="party in sellers" :key="party.name" class="mt-2">
            <strong>{{ party.name }}</strong>
            {{ party.details ? `, ${party.details}` : '' }}
          </p>
        </div>

        <div>
          <p class="font-semibold">{{ content.buyers_heading }}</p>
          <p v-for="party in buyers" :key="party.name" class="mt-2">
            <strong>{{ party.name }}</strong>
            {{ party.details ? `, ${party.details}` : '' }}
          </p>
        </div>

        <p>{{ content.opening }}</p>

        <div
          v-for="clause in clauses"
          :key="`${clause.title}-${clause.paragraphs?.[0]}`"
          class="space-y-3"
        >
          <p v-if="clause.title" class="font-bold">{{ clause.title }}</p>
          <p v-for="paragraph in clause.paragraphs" :key="paragraph">
            {{ paragraph }}
          </p>
        </div>

        <section
          class="my-10 rounded-[10px] border border-lais-border-soft p-5 text-center"
        >
          <h2 class="font-bold">{{ content.awareness?.title }}</h2>
          <p class="mt-3">{{ content.awareness?.text }}</p>
        </section>

        <p class="text-center">{{ content.agreement_date }}</p>

        <div class="grid gap-8 sm:grid-cols-2">
          <div>
            <p class="mb-3 text-center font-semibold">
              {{ t('SALES.RECEIPT_DRAFT.SELLERS') }}
            </p>
            <div
              v-for="party in sellerSignatures"
              :key="party.name"
              class="mb-5 text-center"
            >
              <p>{{ signatureLine }}</p>
              <p>{{ party.name }}</p>
              <p v-if="party.document">{{ party.document }}</p>
            </div>
          </div>
          <div>
            <p class="mb-3 text-center font-semibold">
              {{ t('SALES.RECEIPT_DRAFT.BUYERS') }}
            </p>
            <div
              v-for="party in buyerSignatures"
              :key="party.name"
              class="mb-5 text-center"
            >
              <p>{{ signatureLine }}</p>
              <p>{{ party.name }}</p>
              <p v-if="party.document">{{ party.document }}</p>
            </div>
          </div>
        </div>

        <div class="grid gap-8 sm:grid-cols-[1fr_325px]">
          <div>
            <p class="font-semibold">
              {{ t('SALES.RECEIPT_DRAFT.WITNESSES') }}
            </p>
            <div
              v-for="(witness, index) in witnesses"
              :key="index"
              class="mt-4"
            >
              <p>{{ witnessSignature(index) }}</p>
              <p>{{ witness.name }}</p>
            </div>
          </div>
          <div class="border border-[#D8DAE5] p-5 sm:px-7">
            <p>
              <strong>{{ t('SALES.RECEIPT_DRAFT.OBSERVATION') }}</strong>
              {{ content.observation }}
            </p>
          </div>
        </div>
      </section>
    </div>
    <div class="h-6 rounded-b-[10px] bg-lais-primary" />
  </article>
</template>
